import { baseApi, API_TAGS, createCacheTags, createEndpointBuilder, type Params } from '@/shared/api';

import { receiveConfirmArrivalSegment, receiveConfirmContextSegment } from '../config/constants';
import type {
  ReceiveConfirmArrivalMutationArgs,
  ReceiveConfirmArrivalResultDto,
  ReceiveConfirmContextDto,
  ReceiveConfirmContextQueryArgs
} from '../model/confirm-types';

const receiveEndpoint = createEndpointBuilder('/api/v2/receive');

/**
 * Confirm Arrival & Condition Recording — the pair behind the "Confirm receipt
 * of goods" modal:
 *
 *   open modal          → getReceiveConfirmContext (no `targetLocationId`)
 *   change location     → getReceiveConfirmContext (with `targetLocationId`) —
 *                         re-evaluates storage areas and warnings only
 *   submit              → confirmReceiveArrival
 *
 * The write invalidates the whole `Receive` tag: a receipt consumes incoming
 * balances, so every dashboard/history/losses query that touched this site is
 * now stale. There is **no idempotency key** — a failed submit must re-read the
 * context before being retried, which the invalidation also arranges.
 */
export const receiveConfirmApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getReceiveConfirmContext: builder.query<ReceiveConfirmContextDto, ReceiveConfirmContextQueryArgs>({
      query: ({ shipmentId, ...params }) => ({
        url: receiveEndpoint(receiveConfirmContextSegment(shipmentId)),
        params
      }),
      providesTags: (_result, _error, args) =>
        createCacheTags({
          baseTag: API_TAGS.RECEIVE,
          params: args as unknown as Params
        })
    }),

    confirmReceiveArrival: builder.mutation<ReceiveConfirmArrivalResultDto, ReceiveConfirmArrivalMutationArgs>({
      query: ({ shipmentId, body, ...params }) => ({
        url: receiveEndpoint(receiveConfirmArrivalSegment(shipmentId)),
        method: 'POST',
        params,
        body
      }),
      // Both forms: `createCacheTags` provides `{ type, id: 'Receive' }`, while
      // endpoints that tag by hand provide the bare type.
      invalidatesTags: [API_TAGS.RECEIVE, { type: API_TAGS.RECEIVE, id: API_TAGS.RECEIVE }]
    })
  })
});

export const {
  useGetReceiveConfirmContextQuery,
  useLazyGetReceiveConfirmContextQuery,
  useConfirmReceiveArrivalMutation
} = receiveConfirmApi;
